import type { MeResponse, Page } from '@agent-platform/shared';
import {
  queryOptions,
  useInfiniteQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, retryDelay, setCsrfToken, shouldRetry } from './api';

export const ME_KEY = ['me'] as const;

async function fetchMe(): Promise<MeResponse | null> {
  const { me } = await api<{ me: MeResponse | null }>('/auth/session');
  setCsrfToken(me?.csrfToken ?? null);
  return me;
}

export const meQueryOptions = queryOptions({
  queryKey: ME_KEY,
  queryFn: fetchMe,
  staleTime: 60_000,
  // Survive API restarts (e.g. `pnpm dev` reloading) instead of failing the whole app.
  retry: shouldRetry,
  retryDelay,
});

/** Only valid below the authenticated layout, which guarantees a signed-in user. */
export function useMe(): MeResponse {
  const { data } = useSuspenseQuery(meQueryOptions);
  if (!data) throw new Error('useMe() used outside an authenticated route');
  return data;
}

/** Store a fresh MeResponse (login, accept, tenant switch) and drop all tenant-scoped caches. */
export function useApplyMe(): (me: MeResponse) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (me: MeResponse) => {
      setCsrfToken(me.csrfToken);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
      queryClient.setQueryData(ME_KEY, me);
    },
    [queryClient],
  );
}

export function usePaged<T>(key: readonly unknown[], path: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: key,
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api<Page<T>>(
        `${path}${path.includes('?') ? '&' : '?'}limit=50${pageParam ? `&cursor=${pageParam}` : ''}`,
        { signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
  });
}
